/**
 * V2ActionBarSection — V2-native editor section for step.content.actionBar
 *
 * Edits: Primary and Secondary buttons (text, styles, visibility)
 * V2 paths: content.actionBar.primary/secondary
 *
 * Replaces: OnboardingFooterSection (for V2 context)
 * Reuses: TextElement, SAccordion
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import React, { useCallback, useState, useMemo } from 'react';
import { SAccordion } from '@modules/ui-components';
import { TextElement } from '../../../factories/elements/TextElement';
import { EditorGuideProvider } from '../../../contexts';
import { V2_PRIMARY_BUTTON_CONFIG, V2_SECONDARY_BUTTON_CONFIG } from '../../../config/v2ActionBarConfig';
import { SECTION_MARGIN_BOTTOM } from '../../../config/ui';
import { getValueByPath, isFieldEnabled } from '../../../utils/v2FieldOperations';
import { getV2TranslationValue } from '../../../utils/v2FieldOperations';
import {
  getStyleValues as getStyleValuesUtil,
  getButtonStyleValues as getButtonStyleValuesUtil,
} from '../../../utils/fieldValueOperations';
import { useEditorPresentationProfile } from './PresentationProfileContext';
import {
  getSectionTestId,
  getFieldTestId,
  getCheckboxTestId,
  getStylesToggleTestId,
  getInputTestId,
  getStyleGroupTestId,
} from '../../../utils/testIdUtils';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import type { FlowStepNode } from '@modules/reactFlow/adapters/V2ScreenAdapter';
import type { OnboardingThemeSettingsV2 } from '@modules/firebase-core/converters/onboardingConverter';
import {
  getThemedPrimaryButtonDefaults,
  getThemedSecondaryButtonDefaults,
  getFactoryPrimaryButtonDefaults,
  getFactorySecondaryButtonDefaults,
} from '../../../utils/v2ThemeDefaults';

export interface V2ActionBarSectionProps {
  /** V2 StepNode being edited */
  step: FlowStepNode;

  /** Current editing locale */
  locale: string;

  /** Callback when a V2 path value changes */
  onChange: (path: string, value: unknown) => void;

  /** Whether section is disabled (saving state) */
  disabled?: boolean;

  /** Whether section is expanded by default */
  defaultExpanded?: boolean;

  /** Override accordion title (used by FlexibleLayout for section labels) */
  sectionTitle?: string;

  /** Project ID for icon upload (V2 only) */
  projectId?: string;

  /** Onboarding ID for icon upload (V2 only) */
  onboardingId?: string;

  /** Version ID for icon upload (V2 only) */
  versionId?: string;

  /** Theme settings for themed defaults on element toggle ON */
  themeSettingsV2?: OnboardingThemeSettingsV2 | null;
}

export const V2ActionBarSection: React.FC<V2ActionBarSectionProps> = ({
  step,
  locale,
  onChange,
  disabled = false,
  defaultExpanded = true,
  sectionTitle,
  projectId,
  onboardingId,
  versionId,
  themeSettingsV2,
}) => {
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());

  const toggleFieldExpansion = useCallback((path: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  // Read text value using V2 translations path
  const getFieldValue = useCallback(
    (config: FieldConfig): string => {
      if (config.l10nPath) {
        return getV2TranslationValue(step, config.l10nPath, locale);
      }
      return getValueByPath<string>(step, config.path) ?? '';
    },
    [step, locale]
  );

  const checkFieldEnabled = useCallback(
    (config: FieldConfig): boolean => isFieldEnabled(step, config.path),
    [step]
  );

  const activeProfile = useEditorPresentationProfile();

  const getStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => getStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile]
  );

  const getButtonStyleValues = useCallback(
    (config: FieldConfig): Record<string, unknown> => getButtonStyleValuesUtil(step as unknown as Record<string, unknown>, config, activeProfile),
    [step, activeProfile]
  );

  // Write text value to V2 translations path
  const handleFieldChange = useCallback(
    (config: FieldConfig, value: string) => {
      if (config.l10nPath) {
        onChange(`${config.l10nPath}.translations.${locale}`, value);
      } else {
        onChange(config.path, value);
      }
    },
    [onChange, locale]
  );

  const handleEnabledChange = useCallback(
    (config: FieldConfig, enabled: boolean) => {
      if (enabled) {
        const isPrimary = config.path.includes('primary');
        if (themeSettingsV2) {
          // Onboarding editor: full theme + factory structural defaults
          const themed = isPrimary
            ? getThemedPrimaryButtonDefaults(themeSettingsV2, locale, typeof config.defaultValue === 'string' ? config.defaultValue : 'Continue')
            : getThemedSecondaryButtonDefaults(themeSettingsV2, locale, typeof config.defaultValue === 'string' ? config.defaultValue : '');
          onChange(config.path, themed);
        } else {
          // Theme editor: factory structural defaults (applyToBasicScreen overlays colors)
          const factory = isPrimary
            ? getFactoryPrimaryButtonDefaults(locale, typeof config.defaultValue === 'string' ? config.defaultValue : 'Continue')
            : getFactorySecondaryButtonDefaults(locale, typeof config.defaultValue === 'string' ? config.defaultValue : '');
          onChange(config.path, factory);
        }
      } else {
        onChange(config.path, null);
      }
    },
    [onChange, locale, themeSettingsV2]
  );

  const handleStyleChange = useCallback(
    (stylePath: string, value: unknown) => onChange(stylePath, value),
    [onChange]
  );

  // Memoize Primary Button props
  const primaryProps = useMemo(() => ({
    fieldConfig: V2_PRIMARY_BUTTON_CONFIG,
    value: getFieldValue(V2_PRIMARY_BUTTON_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_PRIMARY_BUTTON_CONFIG),
    locale,
    styleValues: getStyleValues(V2_PRIMARY_BUTTON_CONFIG),
    buttonStyleValues: getButtonStyleValues(V2_PRIMARY_BUTTON_CONFIG),
    expandedStyles: expandedFields.has(V2_PRIMARY_BUTTON_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, getButtonStyleValues, expandedFields]);

  // Memoize Secondary Button props
  const secondaryProps = useMemo(() => ({
    fieldConfig: V2_SECONDARY_BUTTON_CONFIG,
    value: getFieldValue(V2_SECONDARY_BUTTON_CONFIG),
    templateValue: undefined,
    enabled: checkFieldEnabled(V2_SECONDARY_BUTTON_CONFIG),
    locale,
    styleValues: getStyleValues(V2_SECONDARY_BUTTON_CONFIG),
    buttonStyleValues: getButtonStyleValues(V2_SECONDARY_BUTTON_CONFIG),
    expandedStyles: expandedFields.has(V2_SECONDARY_BUTTON_CONFIG.path),
  }), [getFieldValue, checkFieldEnabled, locale, getStyleValues, getButtonStyleValues, expandedFields]);

  const SECTION_NAME = 'Action Bar';

  return (
    <SAccordion
      title={sectionTitle ?? SECTION_NAME}
      collapsible
      defaultExpanded={defaultExpanded}
      sx={{ marginBottom: SECTION_MARGIN_BOTTOM }}
      data-testid={getSectionTestId(SECTION_NAME)}
    >
      <EditorGuideProvider>
        {/* Primary Button */}
        <TextElement
          {...primaryProps}
          onChange={(value) => handleFieldChange(V2_PRIMARY_BUTTON_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_PRIMARY_BUTTON_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_PRIMARY_BUTTON_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          data-testid={getFieldTestId(SECTION_NAME, V2_PRIMARY_BUTTON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_PRIMARY_BUTTON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_PRIMARY_BUTTON_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_PRIMARY_BUTTON_CONFIG.label)}
          styleGroupTestId={getStyleGroupTestId(SECTION_NAME, V2_PRIMARY_BUTTON_CONFIG.label)}
        />

        {/* Secondary Button */}
        <TextElement
          {...secondaryProps}
          onChange={(value) => handleFieldChange(V2_SECONDARY_BUTTON_CONFIG, value)}
          onEnabledChange={(enabled) => handleEnabledChange(V2_SECONDARY_BUTTON_CONFIG, enabled)}
          onStylesExpandToggle={() => toggleFieldExpansion(V2_SECONDARY_BUTTON_CONFIG.path)}
          onStyleChange={handleStyleChange}
          disabled={disabled}
          projectId={projectId}
          onboardingId={onboardingId}
          versionId={versionId}
          data-testid={getFieldTestId(SECTION_NAME, V2_SECONDARY_BUTTON_CONFIG.label)}
          checkboxTestId={getCheckboxTestId(SECTION_NAME, V2_SECONDARY_BUTTON_CONFIG.label)}
          stylesToggleTestId={getStylesToggleTestId(SECTION_NAME, V2_SECONDARY_BUTTON_CONFIG.label)}
          inputTestId={getInputTestId(SECTION_NAME, V2_SECONDARY_BUTTON_CONFIG.label)}
          styleGroupTestId={getStyleGroupTestId(SECTION_NAME, V2_SECONDARY_BUTTON_CONFIG.label)}
        />
      </EditorGuideProvider>
    </SAccordion>
  );
};
